import { useParams } from "react-router-dom";
import { Section } from "@/components/site/Ui";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { useSite } from "@/context/SiteContext";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: [
      ["Who we are", "Qwerjoh Media (\"we\", \"us\") provides online education and creative services for content creators. This policy explains how we handle your personal data in line with UK GDPR and the Data Protection Act 2018."],
      ["What we collect", "Your name, email, WhatsApp number, and any details you provide in our forms or questionnaires (such as your platform, niche and goals). If you buy a plan, we process payment through Stripe; we never see or store your full card details."],
      ["How we use it", "To deliver the service you asked for, send order and questionnaire emails, respond to enquiries, and — only if you consent — send helpful tips and updates. We rely on contract, legitimate interest, and consent as our lawful bases."],
      ["Sharing", "We use trusted processors (e.g. Stripe for payments, our email provider for messages, and secure cloud storage for files). We never sell your data."],
      ["Your rights", "You can ask to access, correct, delete or export your data, or withdraw consent at any time. Email us and we'll help. You can also complain to the ICO (ico.org.uk)."],
      ["Retention", "We keep order records as long as needed for legal and accounting reasons, and marketing contacts until you unsubscribe."],
      ["Contact", "For any privacy question, email us using the address in the footer."],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    body: [
      ["Agreement", "By using our website or buying our services you agree to these terms. Please read them carefully."],
      ["Our services", "We provide educational guidance, personalised content plans, coaching, setup help and video editing. Our materials are for your personal use."],
      ["Payments", "Prices are shown in GBP and processed securely by Stripe. Personalised products begin once you complete your questionnaire."],
      ["Your responsibilities", "You're responsible for creating and publishing your own content and for following each platform's rules. You must own or have rights to any footage you send us."],
      ["Intellectual property", "Plans and materials we deliver are licensed to you for your own use and may not be resold or redistributed."],
      ["Liability", "We provide our services with reasonable care and skill, but we can't guarantee specific results. Nothing here removes your statutory rights."],
      ["Changes", "We may update these terms from time to time. The latest version always applies."],
    ],
  },
  refund: {
    title: "Refund Policy",
    body: [
      ["Personalised digital products", "Our Content Plans and scripts are personalised and made specifically for you. Because work begins as soon as you submit your questionnaire, refunds are limited once we have started."],
      ["Before work starts", "If you have paid but not yet submitted your questionnaire, contact us and we'll usually be able to offer a full refund."],
      ["After delivery", "Once a personalised plan has been delivered, it is generally non-refundable, as it cannot be \"returned\"."],
      ["Your statutory rights", "This policy does not affect your statutory rights under UK consumer law. If something is faulty or not as described, please contact us and we'll put it right."],
      ["How to ask", "Email us with your order reference and we'll help as quickly as we can."],
    ],
  },
  affiliate: {
    title: "Affiliate Disclosure",
    body: [
      ["Honest recommendations", "Some links on our website, especially on the Recommended Gear page, are affiliate links."],
      ["What that means", "If you buy through them, we may earn a small commission at no extra cost to you. We only recommend products we genuinely believe help beginners."],
      ["Amazon Associates", "As an Amazon Associate I earn from qualifying purchases."],
    ],
  },
  earnings: {
    title: "Earnings Disclaimer",
    body: [
      ["No guarantees", "We teach skills and strategies to help you create and grow. Results and earnings vary and are never guaranteed."],
      ["Your effort matters", "Your success depends on many factors including your effort, consistency, niche and audience. Any examples are illustrative, not promises."],
      ["Not financial advice", "Nothing on this site is financial, legal or tax advice."],
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const { settings } = useSite();
  const c = CONTENT[slug] || CONTENT.privacy;
  return (
    <Section className="max-w-3xl">
      <Reveal>
        <Eyebrow>Legal</Eyebrow>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl">{c.title}</h1>
        <p className="mt-3 text-sm text-white/40">Last updated {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</p>
      </Reveal>
      <div className="mt-10 space-y-8">
        {c.body.map(([h, p], i) => (
          <Reveal key={i} delay={i * 0.03}>
            <h2 className="font-display text-xl text-[#D4AF37]">{h}</h2>
            <p className="mt-2 text-white/70 leading-relaxed">{p}</p>
          </Reveal>
        ))}
        <p className="text-white/50 text-sm pt-6 border-t border-white/10">
          Questions?{" "}
          {settings.contact_email ? (
            <>Email <a href={`mailto:${settings.contact_email}`} data-testid="legal-contact-email" className="text-[#D4AF37] hover:underline">{settings.contact_email}</a>.</>
          ) : "Email us."}
        </p>
      </div>
    </Section>
  );
}
